import { createContext, useContext, type ReactNode } from "react";
import { RootStore, rootStore } from "./RootStore";

/**
 * React Context for RootStore
 *
 * Provides the RootStore instance to all components in the tree.
 * This is the recommended way to access stores in React components.
 */
const StoreContext = createContext<RootStore | null>(null);

/**
 * Props for StoreProvider
 */
export interface StoreProviderProps {
  /** Child components that will have access to stores */
  children: ReactNode;
  /**
   * Optional store instance for testing
   * Defaults to the singleton rootStore
   */
  store?: RootStore;
}

/**
 * StoreProvider - Provides RootStore to React component tree
 *
 * Wrap your application with this provider to enable store access
 * via the useStore() hook throughout the component tree.
 *
 * @example
 * // In App.tsx
 * function App() {
 *   return (
 *     <StoreProvider>
 *       <YourApp />
 *     </StoreProvider>
 *   );
 * }
 *
 * @example
 * // In tests with mock store
 * const mockStore = createMockRootStore();
 * render(
 *   <StoreProvider store={mockStore}>
 *     <ComponentUnderTest />
 *   </StoreProvider>
 * );
 */
export function StoreProvider({
  children,
  store = rootStore,
}: StoreProviderProps) {
  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
}

/**
 * Hook to access the StoreContext
 *
 * This is an internal hook. Use useStore() instead for accessing stores.
 *
 * @internal
 * @throws Error if used outside of StoreProvider
 */
export function useStoreContext(): RootStore {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error(
      "useStore must be used within StoreProvider. " +
        "Wrap your app with <StoreProvider>...</StoreProvider>",
    );
  }
  return store;
}
